import { zodResolver } from '@hookform/resolvers/zod';
import { verificationEmailRequestSchema } from '@groundly/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  requestFailedBody,
  requestRefusal,
  useRequestVerificationEmail,
} from '../api/verification-emails.js';
import { useCarriedValues } from '../auth/navigation-state.js';
import { PreAccountGate } from '../auth/PreAccountGate.js';
import { waitBody } from '../auth/signup-errors.js';
import { Button, TextButton } from '../components/Button.js';
import { DangerAlert } from '../components/DangerAlert.js';
import { PageHeading } from '../components/PageHeading.js';
import { TextField } from '../components/TextField.js';
import text from '../components/pre-account-text.module.css';
import { PreAccountCard, PreAccountFootnote, PreAccountFrame } from '../frame/PreAccountFrame.js';

/**
 * Get a new verification email (account sign-up `ux.md`, flow D). Opened from
 * an expired or invalid link (`?link=expired|invalid`, which adds its alert),
 * from Check your email when it has no address, and from the log in screen's
 * "Email address not verified" alert, with the address carried over.
 *
 * The confirmation is true whatever the server found: Groundly sends only when
 * the address has a login that isn't verified, and never says which it was.
 */
const searchSchema = z.object({ link: z.enum(['expired', 'invalid']).optional() });

export const Route = createFileRoute('/verify-email/resend')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: ResendPage,
});

function ResendPage() {
  return (
    <PreAccountGate>
      <Resend />
    </PreAccountGate>
  );
}

interface FormInput {
  email: string;
}

type Alert = { title: string; body: string; role: 'alert' | 'status' };

const LINK_ALERTS: Record<'expired' | 'invalid', Alert> = {
  expired: {
    title: 'This verification link has expired',
    body: "It didn't verify any email address. Enter yours to get a new link.",
    role: 'status',
  },
  invalid: {
    title: "This verification link isn't valid",
    body: "It didn't verify any email address. Enter yours to get a new link.",
    role: 'status',
  },
};

function Resend() {
  const { link } = Route.useSearch();
  const carried = useCarriedValues();
  const [sentTo, setSentTo] = useState<string | null>(null);
  // The link's alert is there when the page loads, so it uses `role="status"`
  // (`patterns/ui-patterns.md`, 8); it goes once the person has acted.
  const [alert, setAlert] = useState<Alert | null>(link === undefined ? null : LINK_ALERTS[link]);
  const [lastEmail, setLastEmail] = useState(carried.email);

  if (sentTo !== null) {
    return (
      <Sent
        email={sentTo}
        onUseDifferent={() => {
          setLastEmail(sentTo);
          setSentTo(null);
        }}
      />
    );
  }

  return (
    <ResendForm
      defaultEmail={lastEmail}
      alert={alert}
      onAlert={setAlert}
      onSent={(email) => {
        setAlert(null);
        setSentTo(email);
      }}
    />
  );
}

function ResendForm({
  defaultEmail,
  alert,
  onAlert,
  onSent,
}: {
  defaultEmail: string;
  alert: Alert | null;
  onAlert: (alert: Alert | null) => void;
  onSent: (email: string) => void;
}) {
  const request = useRequestVerificationEmail();

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm<FormInput, unknown, { email: string }>({
    resolver: zodResolver(verificationEmailRequestSchema),
    defaultValues: { email: defaultEmail },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });

  const email = register('email');

  const onSubmit = handleSubmit((values) => {
    if (request.isPending) return;
    request.mutate(values.email, {
      onSuccess: () => onSent(values.email),
      onError: (error) => {
        const refusal = requestRefusal(error);
        if (refusal.kind === 'field') {
          setError('email', { message: refusal.message });
          setFocus('email');
          return;
        }
        onAlert(
          refusal.kind === 'too-many'
            ? {
                title: 'Too many verification emails requested',
                body: waitBody(refusal.minutes),
                role: 'alert',
              }
            : {
                title: "Couldn't request the verification email",
                body: requestFailedBody(refusal.kind),
                role: 'alert',
              },
        );
      },
    });
  });

  return (
    <PreAccountFrame>
      <PreAccountCard
        as="form"
        labelledBy="resend-heading"
        onSubmit={(event) => void onSubmit(event)}
        head={<PageHeading id="resend-heading">Get a new verification email</PageHeading>}
      >
        {alert === null ? null : (
          <DangerAlert title={alert.title} role={alert.role}>
            {alert.body}
          </DangerAlert>
        )}

        <TextField
          {...email}
          id="email"
          label="Email address"
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          error={errors.email?.message}
          onChange={(event) => {
            void email.onChange(event);
            if (errors.email !== undefined) void trigger('email');
          }}
          onBlur={(event) => {
            void email.onBlur(event);
            if (dirtyFields.email === true) void trigger('email');
          }}
        />

        {/* Marked busy rather than disabled, so focus stays on it (`ux.md`, Focus). */}
        <Button
          type="submit"
          variant="primary"
          block
          aria-disabled={request.isPending || undefined}
        >
          {request.isPending ? 'Sending' : 'Send verification email'}
        </Button>
      </PreAccountCard>

      <PreAccountFootnote>
        Already verified? <Link to="/login">Log in</Link>
      </PreAccountFootnote>
    </PreAccountFrame>
  );
}

function Sent({ email, onUseDifferent }: { email: string; onUseDifferent: () => void }) {
  return (
    <PreAccountFrame>
      <PreAccountCard head={<PageHeading>Check your email</PageHeading>}>
        <p className={text.message}>
          If <span className={text.address}>{email}</span> has a login that isn&apos;t verified yet,
          Groundly sent a new verification email to it. Open the link in it to verify your email
          address and continue to the dashboard.
        </p>
        <p className={text.muted}>
          If this address is already verified,{' '}
          <Link to="/login" state={{ email }}>
            log in
          </Link>{' '}
          instead.
        </p>
        <p className={text.muted}>Can&apos;t find the email? Check your spam or junk folder.</p>
      </PreAccountCard>

      <PreAccountFootnote>
        <TextButton onClick={onUseDifferent}>Use a different email address</TextButton>
      </PreAccountFootnote>
    </PreAccountFrame>
  );
}
